import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';
import { usePermission } from '../../context/PermissionContext';
import useLabEnabled from '../../hooks/useLabEnabled';
import useAdmissionEnabled from '../../hooks/useAdmissionEnabled';
import './FeesBillingTabs.css';

/**
 * Shared tab strip for the Fees & Billing section.
 *
 * Each tab is a real route (not local state) so deep-links + back-button
 * behave naturally. The `active` prop picks which pill is highlighted.
 *
 *   <FeesBillingTabs active="consultation" />
 *   <FeesBillingTabs active="procedures" />
 *   <FeesBillingTabs active="rates" />
 *   <FeesBillingTabs active="lab" />   // only when the Lab module is enabled
 */
const TABS = [
  { key: 'consultation', label: 'Consultation Fees', route: ROUTES.FEES_BILLING },
  { key: 'procedures',   label: 'Procedure Catalog', route: ROUTES.PROCEDURES },
  { key: 'rates',        label: 'Doctor Rate Overrides', route: ROUTES.DOCTOR_RATES },
];

// B2 / S9 — IPD catalog tabs. Join the strip ONLY when the Admission module
// is ON and the viewer holds MANAGE_BED_CONFIG (clone of the Lab-tab join
// below) — an OPD-only / IPD-OFF organisation's strip stays byte-identical.
// B3 appends "Bed & Tariff Setup" (the ward→room→bed builder). The bed-board
// PREVIEW is NOT a settings tab — it's reached via the builder's "Preview board"
// link + the direct /org/bed-board route.
const ADMISSION_TABS = [
  { key: 'bed-categories', label: 'Bed Categories', route: ROUTES.BED_CATEGORIES },
  { key: 'surgery-catalog', label: 'Surgery Catalog', route: ROUTES.SURGERY_CATALOG },
  { key: 'ot-rooms', label: 'OT Rooms', route: ROUTES.OT_ROOMS },
  { key: 'bed-setup', label: 'Bed & Tariff Setup', route: ROUTES.BED_SETUP },
];

// Day-1 IPD / S1 — Charge Items master. Own permission (MANAGE_CHARGE_MASTER,
// distinct from MANAGE_BED_CONFIG) so it joins/leaves the strip independently
// of the other B2 catalogs, same admissionOn-gated pattern.
const CHARGE_ITEMS_TAB = { key: 'charge-items', label: 'Charge items', route: ROUTES.CHARGE_ITEMS };

export default function FeesBillingTabs({ active }) {
  const navigate = useNavigate();
  // The Lab Catalog tab joins the strip only when the module is enabled and the
  // viewer can manage it — so a lab-off org's strip is unchanged.
  const labOn = useLabEnabled();
  const canManageLab = usePermission('MANAGE_LAB_CATALOG');
  const admissionOn = useAdmissionEnabled();
  const canManageBedConfig = usePermission('MANAGE_BED_CONFIG');
  const canManageCharges = usePermission('MANAGE_CHARGE_MASTER');
  const baseTabs =
    labOn && canManageLab
      ? [...TABS, { key: 'lab', label: 'Lab Catalog', route: ROUTES.LAB_CATALOG }]
      : TABS;
  const admissionTabs = admissionOn && canManageBedConfig ? ADMISSION_TABS : [];
  const chargeItemsTabs = admissionOn && canManageCharges ? [CHARGE_ITEMS_TAB] : [];
  const tabs = [...baseTabs, ...admissionTabs, ...chargeItemsTabs];

  return (
    <div className="fbt-tabs" role="tablist">
      {tabs.map((t) => (
        <button
          key={t.key}
          role="tab"
          aria-selected={active === t.key}
          className={`fbt-tab ${active === t.key ? 'fbt-tab-active' : ''}`}
          onClick={() => navigate(t.route)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}
